import classNames from 'classnames'
import React from 'react'
import { ValueLocked } from 'src/components/framework/icons/value-locked'
import { Card, Column, Grid } from 'src/components/framework/ui'
import { CTA } from 'src/components/framework/ui/cta'
import { SectionHeader } from 'src/components/framework/ui/section-header'
import { RatioLink } from 'src/components/page/ratios/ratios'
import { useSecurityContext } from 'src/contexts/security.context'

import * as styles from './price-performance.module.scss'

export const PricePerformance: React.FC = () => {
  const { security } = useSecurityContext()

  return (
    <>
      <Grid>
        <Column msm={12} ts={12} ss={8}>
          <SectionHeader>
            {security.ticker} Share Price Performance
          </SectionHeader>
          <div className={styles.pricePerformance__grids}>
            <DataList data={SHARE_PRICE_DATA} />
            <DataList data={DAYS_CHANGE_DATA} />
          </div>
        </Column>
        <Column msm={12} ts={12} ss={4}>
          <Card
            variants={['outlined']}
            style={{ position: 'relative', height: '150px' }}
          >
            <CTA includeLabel={false} />
          </Card>
        </Column>
      </Grid>
    </>
  )
}

interface DataListProps {
  data: PerformanceData
}

const DataList: React.FC<DataListProps> = ({ data }) => {
  return (
    <ul className={classNames(styles.pricePerformance__grid, 'stk-text-small')}>
      <li
        className={classNames(
          styles.pricePerformance__column,
          'stk-text-strong',
        )}
      >
        {data.label}
        <div>
          <ValueLocked />
        </div>
      </li>
      {data.ratios.map((ratios, index) => (
        <li className={styles.pricePerformance__column} key={index}>
          {ratios.map((ratio, ratioIndex) => (
            <React.Fragment key={ratioIndex}>
              <RatioLink id={ratio.id}>{ratio.name}</RatioLink>
              {ratioIndex + 1 < ratios.length && <>&nbsp;&mdash;&nbsp;</>}
            </React.Fragment>
          ))}
          <div>
            {ratios.map((ratio, ratioIndex) => (
              <React.Fragment key={ratioIndex}>
                <ValueLocked />
                {ratioIndex + 1 < ratios.length && <>&nbsp;&mdash;&nbsp;</>}
              </React.Fragment>
            ))}
          </div>
        </li>
      ))}
    </ul>
  )
}

interface Ratio {
  name: string
  id: string
}

interface PerformanceData {
  label: string
  ratios: ReadonlyArray<ReadonlyArray<Ratio>>
}

const SHARE_PRICE_DATA: PerformanceData = {
  label: 'Share Price',
  ratios: [
    [{ name: 'Volume', id: 'volume-995' }],
    [
      { name: 'Bid', id: 'bid-price-991' },
      { name: 'Ask', id: 'ask-price-992' },
    ],
    [
      { name: 'Low', id: 'days-low-985' },
      { name: 'High', id: 'days-high-984' },
    ],
    [{ name: 'Spread', id: 'bid-ask-spread-5063' }],
  ],
}

const DAYS_CHANGE_DATA: PerformanceData = {
  label: "Day's Change",
  ratios: [
    [{ name: 'Avg Volume', id: 'average-volume-10d-5' }],
    [
      { name: 'Open', id: 'open-price-989' },
      { name: 'Close', id: 'close-price-990' },
    ],
    [{ name: 'Range (12m)', id: '52-week-high-5466' }],
    [{ name: 'Beta', id: 'beta-5114' }],
  ],
}
